/**
 * API client for the `support-tickets` backend resource.
 *
 * Support tickets are help-desk requests with a status/priority/category, a
 * threaded comment trail, and assignment to an agent. CRUD plus comment/assign
 * actions and a stats summary hit the main API under `/support-tickets`. List
 * results are paginated; single-entity responses are unwrapped from the standard
 * `{ success, data }` envelope.
 */
import { apiClient } from './client';

/**
 * A persisted support ticket as returned by the API. `requester_id`,
 * `assigned_to`, and comment `author_id` may be raw id strings or populated
 * `{ _id, name_* }` objects. The `last_reply_*` fields are denormalized helpers
 * for the list view.
 */
export interface SupportTicket {
  _id: string;
  subject: string;
  description: string;
  requester_id: string | { _id: string; name_en?: string; name_ar?: string; email?: string };
  assigned_to?: string | { _id: string; name_en?: string; name_ar?: string; email?: string };
  status: 'open' | 'in_progress' | 'needs_action' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: 'technical' | 'billing' | 'account' | 'tournament' | 'other';
  category_custom?: string;
  is_guest?: boolean;
  phone?: string;
  email?: string;
  guest_name?: string;
  comments: Array<{
    body: string;
    author_id: string | { _id: string; name_en?: string; name_ar?: string; role?: string };
    is_guest_comment?: boolean;
    created_at: string;
  }>;
  resolved_at?: string;
  closed_at?: string;
  /** Timestamp of the most recent comment (any author). */
  last_reply_at?: string;
  /** Who sent the most recent comment. 'user' = ticket requester, 'support' = admin/assignee/etc. */
  last_reply_from?: 'user' | 'support' | null;
  /** Short preview (~160 chars) of the most recent comment, for the table view. */
  last_reply_body?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Query string params for GET `/support-tickets` (filters, date ranges, pagination). */
export interface TicketQueryParams {
  status?: 'open' | 'in_progress' | 'needs_action' | 'resolved' | 'closed';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  category?: 'technical' | 'billing' | 'account' | 'tournament' | 'other';
  category_custom?: string;
  search?: string;
  requester_id?: string;
  assigned_to?: string;
  requester_search?: string;
  last_reply_from?: 'user' | 'support';
  /** YYYY-MM-DD inclusive — ticket created on or after this date. */
  created_from?: string;
  /** YYYY-MM-DD inclusive — ticket created on or before this date. */
  created_to?: string;
  /** YYYY-MM-DD inclusive — last reply on or after this date. */
  reply_from?: string;
  /** YYYY-MM-DD inclusive — last reply on or before this date. */
  reply_to?: string;
  page?: number;
  limit?: number;
}

/** Request body for creating a ticket; `subject` and `description` are required. */
export interface CreateTicketDto {
  subject: string;
  description: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  category?: 'technical' | 'billing' | 'account' | 'tournament' | 'other';
  category_custom?: string;
  status?: 'open' | 'in_progress' | 'needs_action' | 'resolved' | 'closed';
}

/** Request body for updating a ticket; all fields optional, includes reassignment. */
export interface UpdateTicketDto {
  subject?: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  category?: 'technical' | 'billing' | 'account' | 'tournament' | 'other';
  category_custom?: string;
  status?: 'open' | 'in_progress' | 'needs_action' | 'resolved' | 'closed';
  assigned_to?: string;
  requester_search?: string;
}

/** Request body for adding a comment to a ticket's thread. */
export interface AddCommentDto {
  body: string;
}

/** Paginated list result for GET `/support-tickets` (raw envelope, kept as-is). */
export interface TicketsResponse {
  success: boolean;
  data: SupportTicket[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * GET `/support-tickets` — list tickets with optional filters and pagination.
 * @param params optional {@link TicketQueryParams}.
 * @returns the full {@link TicketsResponse} envelope (data + pagination).
 */
export async function getTickets(params?: TicketQueryParams): Promise<TicketsResponse> {
  const { data } = await apiClient.get<{ success: boolean; data: SupportTicket[]; pagination: any }>(
    '/support-tickets',
    { params }
  );
  return data as TicketsResponse;
}

/**
 * GET `/support-tickets/:id` — fetch a single ticket (with its comment thread).
 * @param id ticket `_id`.
 * @returns the {@link SupportTicket}.
 */
export async function getTicket(id: string): Promise<SupportTicket> {
  const { data } = await apiClient.get<{ success: boolean; data: SupportTicket }>(`/support-tickets/${id}`);
  return data.data;
}

/**
 * POST `/support-tickets` — create a ticket.
 * @param dto {@link CreateTicketDto} payload.
 * @returns the newly created {@link SupportTicket}.
 */
export async function createTicket(dto: CreateTicketDto): Promise<SupportTicket> {
  const { data } = await apiClient.post<{ success: boolean; data: SupportTicket }>('/support-tickets', dto);
  return data.data;
}

/**
 * PATCH `/support-tickets/:id` — partially update a ticket (status, priority, reassign, etc.).
 * @param id ticket `_id`.
 * @param dto {@link UpdateTicketDto} with the fields to change.
 * @returns the updated {@link SupportTicket}.
 */
export async function updateTicket(id: string, dto: UpdateTicketDto): Promise<SupportTicket> {
  const { data } = await apiClient.patch<{ success: boolean; data: SupportTicket }>(`/support-tickets/${id}`, dto);
  return data.data;
}

/**
 * POST `/support-tickets/:id/comments` — append a comment to the ticket thread.
 * @param id ticket `_id`.
 * @param dto {@link AddCommentDto} containing the comment body.
 * @returns the updated {@link SupportTicket} (with the new comment included).
 */
export async function addComment(id: string, dto: AddCommentDto): Promise<SupportTicket> {
  const { data } = await apiClient.post<{ success: boolean; data: SupportTicket }>(`/support-tickets/${id}/comments`, dto);
  return data.data;
}

/**
 * POST `/support-tickets/:id/assign` — assign the ticket to an agent.
 * Sends `{ assigned_to: assignedToId }` as the body.
 * @param id ticket `_id`.
 * @param assignedToId user `_id` of the assignee.
 * @returns the updated {@link SupportTicket}.
 */
export async function assignTicket(id: string, assignedToId: string): Promise<SupportTicket> {
  const { data } = await apiClient.post<{ success: boolean; data: SupportTicket }>(`/support-tickets/${id}/assign`, {
    assigned_to: assignedToId,
  });
  return data.data;
}

/**
 * DELETE `/support-tickets/:id` — soft-delete a ticket.
 * @param id ticket `_id`.
 */
export async function deleteTicket(id: string): Promise<void> {
  await apiClient.delete(`/support-tickets/${id}`);
}

/** Request body for escalating a ticket (creates a task + sets status needs_action). */
export interface EscalateTicketDto {
  /** The staff member who will do the action. */
  assignee_id: string;
  /** What needs to be done. */
  action_details: string;
  /** Deadline (YYYY-MM-DD). */
  due_date?: string;
  /** The user the action relates to (defaults to the ticket's reporter). */
  related_user_id?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
}

/**
 * POST `/support-tickets/:id/escalate` — mark the ticket `needs_action` and
 * create a task for the chosen assignee. Returns `{ ticket, task }`.
 */
export async function escalateTicket(id: string, dto: EscalateTicketDto): Promise<{ ticket: SupportTicket; task: unknown }> {
  const { data } = await apiClient.post<{ success: boolean; data: { ticket: SupportTicket; task: unknown } }>(
    `/support-tickets/${id}/escalate`, dto,
  );
  return data.data;
}

/** Counts of tickets by status plus the overall total, for dashboard summary cards. */
export interface TicketStats {
  open: number;
  in_progress: number;
  needs_action?: number;
  resolved: number;
  closed: number;
  total: number;
}

/**
 * GET `/support-tickets/stats` — counts of tickets per status plus the total.
 * @returns the {@link TicketStats}.
 */
export async function getTicketStats(): Promise<TicketStats> {
  const { data } = await apiClient.get<{ success: boolean; data: TicketStats }>(
    '/support-tickets/stats',
  );
  return data.data;
}

export async function sendTypingIndicator(ticketId: string, requesterId?: string) {
  await apiClient.post(`/support-tickets/${ticketId}/typing`, { requesterId }).catch(() => {});
}
